import {Decimal} from 'decimal.js';

const decimal10 = new Decimal('10');

/*
 * Return the smallest integer z for which x can be represented as z * 10 ^ k, e.g.:
 *     0.123 = 123*10^-3 so significandAsInteger(0.123) = 123
 *     120 = 12*10^1 so significandAsInteger(120) = 12
 */
function significandAsInteger(x: Decimal): Decimal {
  const prec = x.precision();
  const largestDigitPow = Number(x.abs().log(10).floor());
  return x.mul(decimal10.pow(prec - largestDigitPow - 1));
}

function factorOutTwosAndFives(x: Decimal): Decimal {
  while (x.mod(2).isZero()) {
    x = x.divToInt(2);
  }
  while (x.mod(5).isZero()) {
    x = x.divToInt(5);
  }
  return x;
}

/*
 * Return True if p/q can be represented in decimal notation with a finite number of digits.
 *
 * IMPORTANT: Assumes p and q are not repeating decimals. If p or q are already repeating decimals
 * represented with a finite number of digits, this function makes no attempt to detect that.
 * E.g. p=0.33333 q=1 returns True.
 *
 * Stated another way, this returns True if there is an integer z, for which p/q = z*10^k.
 * e.g. 1/8 = 2^-3 = 5^3*10^-3
 * We determine this as follows:
 * - determine the smallest integer x such that p = x * 10^k
 * - next determine the smallest integer y such that q = y * 5^l * 2^m
 * - if y divides x then p/q = r * 5^a * 2^b = z*10^n
 */
export default function hasFiniteDecimalRepresentation(p: Decimal, q: Decimal): boolean {
  const x = significandAsInteger(p);
  const y = factorOutTwosAndFives(significandAsInteger(q));
  return x.mod(y).isZero();
}
